import { useEffect, useRef } from 'react';

// ADMINSEC-3c-2b — Cloudflare Turnstile widget (customer-app, shared by tenant + vendor
// login). Ported UNCHANGED from the 3c-2a admin-portal widget: loads the Turnstile script
// once, renders the widget explicitly with the public site-key, surfaces the solved token.
// Managed mode (mostly invisible). Rendered ONLY when CAPTCHA is enabled (config endpoint
// reports enabled + a site-key); otherwise the page never mounts this.

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback&render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    onloadTurnstileCallback?: () => void;
  }
}

interface TurnstileWidgetProps {
  siteKey: string;
  /** Called with the token when the challenge is solved. */
  onToken: (token: string) => void;
  /** Called when the token expires or the widget errors (clear the held token). */
  onExpire?: () => void;
}

export function TurnstileWidget({ siteKey, onToken, onExpire }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Hold callbacks in refs so the render effect depends ONLY on siteKey (inline parent
  // callbacks won't churn the widget — a re-render would otherwise reset the solved token).
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;

  useEffect(() => {
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;

    function renderWidget() {
      if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onTokenRef.current(token),
        'expired-callback': () => onExpireRef.current?.(),
        'error-callback': () => onExpireRef.current?.(),
        theme: 'auto',
      });
    }

    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile"]')) {
      // First mount on this page load — inject the script with the onload hook.
      window.onloadTurnstileCallback = renderWidget;
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else {
      // Script tag exists but the API isn't ready yet — poll briefly.
      poll = setInterval(() => {
        if (window.turnstile) {
          if (poll) clearInterval(poll);
          renderWidget();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone — noop */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} className="flex justify-center" />;
}
